/** @typedef {'light' | 'dark'} CheckerColor */

/**
 * @typedef {object} GameState
 * @property {number} stateVersion
 * @property {Record<number, CheckerColor[]>} points
 * @property {CheckerColor[]} bar
 * @property {CheckerColor[]} trayLeft
 * @property {CheckerColor[]} trayRight
 * @property {{ brown: [number, number], white: [number, number] }} dice
 */

/** Standard opening: point number 1–24 -> stack (center toward tip order per legacy DOM) */
const OPENING = {
  1: ["light", "light"],
  6: ["dark", "dark", "dark", "dark", "dark"],
  8: ["dark", "dark", "dark"],
  12: ["light", "light", "light", "light", "light"],
  13: ["dark", "dark", "dark", "dark", "dark"],
  17: ["light", "light", "light"],
  19: ["light", "light", "light", "light", "light"],
  24: ["dark", "dark"],
};

/**
 * @returns {import('./state.js').GameState}
 */
export function createInitialGameState() {
  /** @type {Record<number, CheckerColor[]>} */
  const points = {};
  for (let i = 1; i <= 24; i += 1) {
    points[i] = OPENING[i] ? [...OPENING[i]] : [];
  }
  return {
    stateVersion: 1,
    points,
    bar: [],
    trayLeft: [],
    trayRight: [],
    dice: { brown: [1, 1], white: [1, 1] },
  };
}

/**
 * @param {unknown} v
 * @returns {[number, number]}
 */
function normalizeDicePair(v) {
  if (!Array.isArray(v) || v.length < 2) {
    return [1, 1];
  }
  const clamp = (n) => {
    const x = typeof n === "number" ? n : Number(n);
    if (!Number.isFinite(x)) return 1;
    const i = Math.floor(x);
    if (i < 1 || i > 6) return 1;
    return i;
  };
  return [clamp(v[0]), clamp(v[1])];
}

/**
 * @param {unknown} c
 * @returns {CheckerColor | null}
 */
function normalizeCheckerToken(c) {
  if (typeof c !== "string") return null;
  const s = c.trim().toLowerCase();
  if (s === "light" || s === "white") return "light";
  if (s === "dark" || s === "black" || s === "brown") return "dark";
  return null;
}

/**
 * @param {Record<number, CheckerColor[]>} points
 * @param {CheckerColor[]} bar
 * @param {CheckerColor[]} trayLeft
 * @param {CheckerColor[]} trayRight
 */
function countCheckers(points, bar, trayLeft, trayRight) {
  let n = bar.length + trayLeft.length + trayRight.length;
  for (let i = 1; i <= 24; i += 1) {
    n += (points[i] ?? []).length;
  }
  return n;
}

/**
 * @param {unknown} raw
 * @returns {import('./state.js').GameState}
 */
export function parseGameState(raw) {
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return createInitialGameState();
    }
  }
  if (!obj || typeof obj !== "object") {
    return createInitialGameState();
  }
  const o = /** @type {Record<string, unknown>} */ (obj);
  const pointsObj = o.points && typeof o.points === "object" ? /** @type {Record<string, unknown>} */ (o.points) : null;
  const pointsEmpty =
    !pointsObj ||
    Object.keys(pointsObj).length === 0 ||
    Object.keys(pointsObj).every((k) => !Array.isArray(pointsObj[k]) || /** @type {unknown[]} */ (pointsObj[k]).length === 0);
  if (pointsEmpty) {
    return createInitialGameState();
  }
  const points = {};
  for (let i = 1; i <= 24; i += 1) {
    const arr = pointsObj[String(i)];
    points[i] = Array.isArray(arr)
      ? /** @type {CheckerColor[]} */ (arr.map(normalizeCheckerToken).filter((c) => c === "light" || c === "dark"))
      : [];
  }
  const bar = Array.isArray(o.bar)
    ? /** @type {CheckerColor[]} */ (o.bar.map(normalizeCheckerToken).filter((c) => c === "light" || c === "dark"))
    : [];
  const trayLeft = Array.isArray(o.trayLeft)
    ? /** @type {CheckerColor[]} */ (o.trayLeft.map(normalizeCheckerToken).filter((c) => c === "light" || c === "dark"))
    : [];
  const trayRight = Array.isArray(o.trayRight)
    ? /** @type {CheckerColor[]} */ (o.trayRight.map(normalizeCheckerToken).filter((c) => c === "light" || c === "dark"))
    : [];
  const stateVersion =
    typeof o.stateVersion === "number"
      ? o.stateVersion
      : typeof o.state_version === "number"
        ? o.state_version
        : 1;
  const rawDice = o.dice && typeof o.dice === "object" ? /** @type {Record<string, unknown>} */ (o.dice) : null;
  const dice = {
    brown: normalizeDicePair(rawDice?.brown),
    white: normalizeDicePair(rawDice?.white),
  };

  const built = {
    stateVersion,
    points,
    bar,
    trayLeft,
    trayRight,
    dice,
  };
  if (countCheckers(points, bar, trayLeft, trayRight) === 0) {
    const fresh = createInitialGameState();
    fresh.stateVersion = stateVersion;
    fresh.dice = dice;
    return fresh;
  }
  return built;
}

/**
 * @param {import('./state.js').GameState} state
 */
export function serializeGameState(state) {
  return JSON.stringify({
    stateVersion: state.stateVersion,
    points: state.points,
    bar: state.bar,
    trayLeft: state.trayLeft,
    trayRight: state.trayRight,
    dice: state.dice,
  });
}
